import React, { useCallback, useContext, useMemo } from "react"
import { useAppDispatch } from '../../store'
import { createStackNavigator } from "@react-navigation/stack"
import { useNavigation } from "@react-navigation/native"
import ScreenHeader from "../../utility/ScreenHeader"
import OnboardingLayout from "../../utility/onboarding-layout"
import Welcome from "../../user-onboarding-v2/welcome"
import BasicInfo from "../../user-onboarding-v2/basic-info"
import ValuesSelection from "../../user-onboarding-v2/values-selection"
import AddAccounts from "../../user-onboarding-v2/add-accounts"
import OnboardingComplete from "../../user-onboarding-v2/onboarding-complete"
import Preferences from "../../user-onboarding-v2/preferences"
import { completeOnboarding } from "../../auth-slice"
import Analytics from "../../utility/analytics"
import { ThemeContext } from "../../utility/context/ThemeContext"

const Stack = createStackNavigator<any>()

export const onboardingSteps = [
  {
    name: 'Welcome',
    title: 'Welcome',
    component: Welcome,
  },
  {
    name: "Preferences",
    title: "Preferences",
    component: Preferences,
  },
  {
    name: 'ValueSelection',
    title: 'Value Selection',
    component: ValuesSelection,
  },
  {
    name: 'AddAccounts',
    title: 'Add Accounts',
    component: AddAccounts,
  },
  {
    name: 'BasicInfo',
    title: 'Basic Info',
    component: BasicInfo,
  },
  {
    name: 'Complete',
    title: 'Complete',
    component: OnboardingComplete,
  },
]

const OnboardingStepScreen = ({ route }: { route: { params?: { stepIndex?: number } } }) => {
  const dispatch = useAppDispatch()
  const navigation = useNavigation<any>()
  const stepIndex = route.params?.stepIndex ?? 0
  const currentStep = onboardingSteps[stepIndex]
  const StepComponent: any = currentStep.component

  const nextStep = async () => {
    if (stepIndex + 1 < onboardingSteps.length) {
      Analytics.track('onboarding_step_next', {
        step: onboardingSteps[stepIndex + 1].name,
        step_index: stepIndex + 1,
      })
      navigation.navigate(onboardingSteps[stepIndex + 1].name)
      return
    }

    Analytics.track('onboarding_complete', {
      step: currentStep.name,
      step_index: stepIndex,
    })
    dispatch(completeOnboarding())
  }

  const onPrevious = () => {
    if (stepIndex > 0) {
      Analytics.track('onboarding_step_previous', {
        step: onboardingSteps[stepIndex - 1].name,
        step_index: stepIndex - 1,
      })
      navigation.navigate(onboardingSteps[stepIndex - 1].name, { back: true })
    }
  }

  return (
    <OnboardingLayout
      stepIndex={stepIndex}
      totalSteps={onboardingSteps.length}
      title={currentStep.title}
      onNext={nextStep}
      onBack={onPrevious}
    >
      {({ setStepHandler, setNextEnabled, loading, setLoading }) => (
        <StepComponent
          setNextEnabled={setNextEnabled}
          setStepHandler={setStepHandler}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </OnboardingLayout>
  )
}

const OnboardingStack = (): JSX.Element => {
  const { theme } = useContext(ThemeContext)
  const stepIndexByName = useMemo(() => {
    return Object.fromEntries(onboardingSteps.map((step, index) => [step.name, index]))
  }, [])

  const renderOnboardingHeader = useCallback(({ route }: { route: { name: string } }) => {
    const stepIndex = stepIndexByName[route.name] ?? 0
    return (
      <ScreenHeader
        title="Onboarding"
        functionality={stepIndex < onboardingSteps.length - 1 ? "complete-onboarding" : undefined}
      />
    )
  }, [stepIndexByName])

  const screenOptions = useMemo(() => ({
    gestureEnabled: false,
    animation: 'none' as const,
    cardStyle: { backgroundColor: theme.colors.bgMain },
    header: renderOnboardingHeader,
  }), [renderOnboardingHeader, theme.colors.bgMain])

  return (
    <Stack.Navigator
      id={undefined}
      screenOptions={screenOptions}
      detachInactiveScreens={false}
    >
      {
        onboardingSteps.map((step, index) => (
          <Stack.Screen
            key={step.name}
            name={step.name}
            component={OnboardingStepScreen}
            initialParams={{ stepIndex: index }}
            options={{
              title: step.title,
              animation: "none"
            }}
          />
        ))
      }
    </Stack.Navigator>
  )
}

export default OnboardingStack


